/**
 * Registry that lets a product page hand its Sofia-generated summary to the
 * Sofia panel, which drops it into the chat as one of Sofia's messages.
 *
 * The summary is owned by the page (run detail knows about `run_summary`, the
 * simulations table knows about its own rollup) but is presented by Sofia's
 * panel, which lives in the app-covi module. app-covi already
 * depends on runs/agents/home, so importing a page module back from it would
 * close a module-graph cycle — this decoupled store sidesteps that the same way
 * `coviEvents.ts` does for opening the panel.
 *
 * A registration symbol scopes writes to the page that claimed the slot, so a
 * route change that mounts the next page before unmounting the previous one
 * cannot have the outgoing page clear the incoming page's summary.
 */

export interface SofiaPageSummary {
  /** Heading the summary is filed under, e.g. "Run Summary". */
  title: string
  /** The summary body as markdown. Shown verbatim — never re-generated. */
  markdown: string
  /**
   * Identity of what the summary describes (a run id, a timeframe key). A
   * summary is presented once per subject, so this is what decides whether the
   * user has already been shown this one.
   */
  sourceId: string
  /**
   * Opens a Sofia thread on this summary. The page owns it so it can record its
   * own analytics and supply its own thread title; the panel supplies no
   * default.
   */
  onPresent: () => void
}

let activeRegistration: symbol | null = null
let snapshot: SofiaPageSummary | null = null
const listeners = new Set<() => void>()

function emitChange(): void {
  listeners.forEach((listener) => listener())
}

/**
 * Claim the summary slot for one page. The returned `unregister` only clears
 * the slot while this registration is still the active one.
 */
export function registerSofiaPageSummary(): {
  registration: symbol
  unregister: () => void
} {
  const registration = Symbol("sofia-page-summary")
  activeRegistration = registration

  return {
    registration,
    unregister: () => {
      if (activeRegistration !== registration) {
        return
      }
      activeRegistration = null
      snapshot = null
      emitChange()
    },
  }
}

/** Publish (or clear, with `null`) the summary for a held registration. */
export function publishSofiaPageSummary(
  registration: symbol,
  nextSummary: SofiaPageSummary | null,
): void {
  if (activeRegistration !== registration) {
    return
  }
  snapshot = nextSummary
  emitChange()
}

export function subscribeSofiaPageSummary(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function getSofiaPageSummarySnapshot(): SofiaPageSummary | null {
  return snapshot
}

export function getSofiaPageSummaryServerSnapshot(): SofiaPageSummary | null {
  return null
}
